import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api } from "@/lib/api";

export const DAG_INDEX = { Maandag: 0, Dinsdag: 1, Woensdag: 2, Donderdag: 3, Vrijdag: 4, Zaterdag: 5, Zondag: 6 };

// v20 B: "Extra gedaan" onder een dag — gewone regels, geen vinkje; telt nergens mee. Alleen de eigenaar voegt toe of wist.
export const ExtraGedaan = ({ dag, week, items, eigen, onAdd, onDelete }) => {
  const d = DAG_INDEX[dag];
  const lijst = items.filter((x) => x.dag === d);
  const [open, setOpen] = useState(false);
  const [tekst, setTekst] = useState("");
  const [bezig, setBezig] = useState(false);
  if (!eigen && !lijst.length) return null;
  const opslaan = async () => {
    const t = tekst.trim();
    if (!t || bezig) return;
    setBezig(true);
    try {
      const r = await api.post("/weekritme/extra", { week, dag: d, tekst: t });
      onAdd(r.data); setTekst(""); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt. Je tekst staat nog in het vak."); }
    finally { setBezig(false); }
  };
  const wis = async (x) => {
    try { await api.delete(`/weekritme/extra/${x.id}`); onDelete(x.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
  };
  return (
    <div data-testid={`weekritme-extra-${d}`} className="px-4 py-2.5 bg-zinc-50/60">
      {lijst.length > 0 && <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Extra gedaan · telt niet mee</p>}
      <ul className="space-y-1">
        {lijst.map((x) => (
          <li key={x.id} data-testid={`weekritme-extra-regel-${x.id}`} className="flex items-start gap-2 text-sm text-zinc-700">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400" />
            <span className="flex-1 break-words">{x.tekst}</span>
            {eigen && <button type="button" aria-label="Weghalen" data-testid={`weekritme-extra-wis-${x.id}`} onClick={() => wis(x)}
              className="h-8 w-8 sm:h-6 sm:w-6 shrink-0 flex items-center justify-center rounded text-zinc-400 hover:text-rose-600 hover:bg-white"><X className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
      {eigen && (open ? (
        <div className="flex gap-2 mt-1.5">
          <input autoFocus data-testid={`weekritme-extra-input-${d}`} maxLength={200} value={tekst} onChange={(e) => setTekst(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") opslaan(); if (e.key === "Escape") setOpen(false); }}
            placeholder="Wat deed je extra?" className="flex-1 min-w-0 min-h-[40px] px-2.5 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <button type="button" data-testid={`weekritme-extra-opslaan-${d}`} disabled={bezig || !tekst.trim()} onClick={opslaan}
            className="shrink-0 px-3 rounded-md bg-zinc-950 text-white text-sm font-semibold disabled:opacity-40">Toevoegen</button>
        </div>
      ) : (
        <button type="button" data-testid={`weekritme-extra-toevoegen-${d}`} onClick={() => setOpen(true)}
          className="mt-1 inline-flex items-center gap-1 min-h-[36px] text-xs font-semibold text-zinc-500 hover:text-zinc-900"><Plus className="h-3.5 w-3.5" /> Iets extra</button>
      ))}
    </div>
  );
};

const DAG_KORT = ["ma", "di", "wo", "do", "vr", "za", "zo"];
export const aantalExtra = (items, dag) => items.filter((x) => x.dag === DAG_INDEX[dag]).length;

// v20-R1 R9: de eigenaar zet ook iets extra op een dag zonder taken.
export const ExtraAndereDag = ({ week, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [dag, setDag] = useState(0);
  const [tekst, setTekst] = useState("");
  const [bezig, setBezig] = useState(false);
  const opslaan = async () => {
    const t = tekst.trim();
    if (!t || bezig) return;
    setBezig(true);
    try { const r = await api.post("/weekritme/extra", { week, dag, tekst: t }); onAdd(r.data); setTekst(""); setOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt. Je tekst staat nog in het vak."); }
    finally { setBezig(false); }
  };
  if (!open) return (
    <button type="button" data-testid="weekritme-extra-andere-dag" onClick={() => setOpen(true)}
      className="mt-3 inline-flex items-center gap-1 min-h-[40px] text-xs font-semibold text-zinc-500 hover:text-zinc-900"><Plus className="h-3.5 w-3.5" /> Extra op een andere dag</button>
  );
  return (
    <div data-testid="weekritme-extra-andere-dag-form" className="mt-3 flex flex-wrap items-center gap-2 rounded-[10px] border border-zinc-200 bg-white px-3 py-2.5">
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Extra gedaan op</span>
      <div className="flex gap-1" role="radiogroup" aria-label="Dag">
        {DAG_KORT.map((k, i) => (
          <button key={k} type="button" role="radio" aria-checked={dag === i} data-testid={`weekritme-extra-andere-dag-${i}`} onClick={() => setDag(i)}
            className={`h-9 w-9 rounded-md text-xs font-semibold ${dag === i ? "bg-zinc-950 text-white" : "border border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>{k}</button>
        ))}
      </div>
      <input autoFocus data-testid="weekritme-extra-andere-dag-input" maxLength={200} value={tekst} onChange={(e) => setTekst(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") opslaan(); if (e.key === "Escape") setOpen(false); }}
        placeholder="Wat deed je extra?" className="flex-1 min-w-[12rem] min-h-[40px] px-2.5 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
      <button type="button" data-testid="weekritme-extra-andere-dag-opslaan" disabled={bezig || !tekst.trim()} onClick={opslaan}
        className="px-3 min-h-[40px] rounded-md bg-zinc-950 text-white text-sm font-semibold disabled:opacity-40">Toevoegen</button>
    </div>
  );
};
